import { useAuthInfo } from '@propelauth/react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { deleteFactFromVisualisation } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/components/visual-timeline/syncing-helpers/syncVisualisationWithTimelineChanges';

export const useDeleteChronologyFactWithSync = (matterId: string, subsetId: string, chronologyId: string) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();
  const authInfo = useAuthInfo();

  return useMutation(
    async (factId: string) => {
      const fetchConfig = getFetchConfig({ method: FetchMethod.DELETE });

      const response = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/fact/${factId}`,
        fetchConfig,
      );

      if (!response.ok) {
        toast.error('Failed to delete fact');
        throw new Error('Failed to delete fact');
      }

      return factId;
    },
    {
      onSuccess: async (factId: string) => {
        // Invalidate chronology query first
        queryClient.invalidateQueries([QueryEntity.Chronology, { chronologyId }]);
        queryClient.invalidateQueries([QueryEntity.ChronologyFacts, { chronologyId }]);

        // Sync with visual timeline
        try {
          await deleteFactFromVisualisation({
            matterId,
            subsetId,
            chronologyId,
            nodeId: factId,
            getAccessToken: authInfo.tokens.getAccessToken,
          });

          queryClient.invalidateQueries([QueryEntity.ChronologyVisualisation, { subsetId, chronologyId }]);
          toast.success('Fact removed');
        } catch (error) {
          console.error('Failed to sync deleted fact with visual timeline:', error);
        }
      },
      onError: (error) => {
        console.error('Delete Error: ', error);
        toast.error('Failed to delete fact');
      },
    },
  );
};
